import { useState, useRef } from 'react'
import { uploadFile } from '../services/api'
import { useTelegram } from '../hooks/useTelegram'
import './ImageUploader.css'

interface ImageUploaderProps {
  onUploadSuccess?: (filename: string, url: string, file: File) => void
  onUploadError?: (error: string) => void
  maxSize?: number // в MB
  accept?: string
  label?: string
}

function ImageUploader({
  onUploadSuccess,
  onUploadError,
  maxSize = 10,
  accept = 'image/*',
  label = 'Загрузить изображение'
}: ImageUploaderProps) {
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { showAlert } = useTelegram()

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Валидация размера
    if (file.size > maxSize * 1024 * 1024) {
      const errorMsg = `Файл слишком большой. Максимум: ${maxSize}MB`
      setError(errorMsg)
      showAlert(errorMsg)
      return
    }

    // Валидация типа
    if (!file.type.startsWith('image/')) {
      const errorMsg = 'Выберите файл изображения'
      setError(errorMsg)
      showAlert(errorMsg)
      return
    }

    // Превью
    const reader = new FileReader()
    reader.onload = (e) => {
      setPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)

    // Загрузка
    setUploading(true)
    setError(null)

    try {
      const response = await uploadFile(file)
      if (response.success) {
        onUploadSuccess?.(response.filename, URL.createObjectURL(file), file)
        showAlert('Изображение загружено успешно!')
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Ошибка при загрузке'
      setError(errorMsg)
      onUploadError?.(errorMsg)
      showAlert(errorMsg)
    } finally {
      setUploading(false)
    }
  }

  const handleClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="image-uploader">
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      
      {preview ? (
        <div className="preview-container">
          <img src={preview} alt="Preview" className="preview-image" />
          <button
            className="upload-button"
            onClick={handleClick}
            disabled={uploading}
          >
            {uploading ? 'Загрузка...' : 'Выбрать другое'}
          </button>
        </div>
      ) : (
        <button
          className="upload-button"
          onClick={handleClick}
          disabled={uploading}
        >
          {uploading ? 'Загрузка...' : label}
        </button>
      )}

      {error && <div className="error-message">{error}</div>}
    </div>
  )
}

export default ImageUploader

